import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";
import { ArrowUpRight, Check, IndianRupee, Phone } from "lucide-react";
import { api, formatApiError, PHONE, PHONE_LABEL, WHATSAPP } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", company: "", site_address: "", required_capacity: "", start_date: "", rental_duration: "", notes: "" };
const EMPTY_QUERY = { name: "", phone: "", message: "" };

export const Booking = ({ prefill, onPrefilled }) => {
  const [mode, setMode] = useState("book");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [payCfg, setPayCfg] = useState(null);
  const [paying, setPaying] = useState(false);
  const [qForm, setQForm] = useState(EMPTY_QUERY);
  const [qDone, setQDone] = useState(false);
  const [qLoading, setQLoading] = useState(false);
  const [qError, setQError] = useState("");
  const { Razorpay } = useRazorpay();

  useEffect(() => { api.get("/payments/config").then((r) => setPayCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (prefill) { setMode("book"); setForm((f) => ({ ...f, required_capacity: prefill })); onPrefilled(); }
  }, [prefill, onPrefilled]);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const qUpdate = (e) => setQForm({ ...qForm, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/rental-inquiries", form);
      setDone({ inquiryId: data.inquiry_id, name: form.name, phone: form.phone, paid: false });
      toast.success("Request sent — we will call you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const submitQuery = async (e) => {
    e.preventDefault();
    setQError("");
    setQLoading(true);
    try {
      await api.post("/queries", qForm);
      setQDone(true);
      toast.success("Query sent — we will get back to you shortly.");
    } catch (err) {
      setQError(formatApiError(err));
      toast.error("Query could not be sent from this browser.");
    } finally { setQLoading(false); }
  };

  const whatsappQuery = `https://wa.me/916913828054?text=${encodeURIComponent(`Query: ${qForm.message || ""} — ${qForm.name || ""} (${qForm.phone || ""})`)}`;

  const payAdvance = async () => {
    setPaying(true);
    try {
      const { data: order } = await api.post("/payments/order", { inquiry_id: done.inquiryId });
      const rzp = new Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: "M/S KRISHNA GENERATOR SERVICE", description: "DG booking advance",
        prefill: { name: done.name, contact: done.phone }, theme: { color: "#FF6B00" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", { ...resp, inquiry_id: done.inquiryId });
            setDone({ ...done, paid: true });
            toast.success("Advance received. Your booking is confirmed.");
          } catch { toast.error("Payment could not be verified. Call us and we will sort it out."); }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setPaying(false); }
  };

  return (
    <section id="booking" className="booking">
      <div className="booking-inner">
        <motion.div className="booking-copy" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <p className="eyebrow">START YOUR REQUEST</p>
          <h2 className="sec-title">Let’s keep<br /><em>you moving.</em></h2>
          <p>Book a DG set or just ask a question — we review every message personally and call back with availability and a clear quote.</p>
          <div className="contact-rows">
            <a data-testid="booking-phone-link" href={`tel:${PHONE}`}><Phone size={16} /> {PHONE_LABEL}</a>
            <a data-testid="booking-whatsapp-link" href={WHATSAPP} target="_blank" rel="noreferrer"><span className="wa-dot">W</span> WhatsApp us</a>
          </div>
        </motion.div>
        <motion.div className="bform-wrap" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}>
          <div className="bform-tabs">
            <button data-testid="booking-mode-book" className={mode === "book" ? "bform-tab on" : "bform-tab"} onClick={() => setMode("book")}>BOOK A DG SET</button>
            <button data-testid="booking-mode-query" className={mode === "query" ? "bform-tab on" : "bform-tab"} onClick={() => setMode("query")}>ASK A QUESTION</button>
          </div>
          {mode === "query" ? (
            qDone ? (
              <div className="success" data-testid="query-success-message">
                <div className="success-icon"><Check size={26} /></div>
                <h3>Query sent.</h3>
                <p>Thanks, {qForm.name}. Our team will reach you on {qForm.phone} shortly.</p>
                <button data-testid="new-query-button" className="btn btn-ghost" onClick={() => { setQDone(false); setQForm(EMPTY_QUERY); }}>Ask another question</button>
              </div>
            ) : (
              <form data-testid="query-form" className="bform" onSubmit={submitQuery}>
                <p className="f-step"><span>?</span> ASK US ANYTHING</p>
                <div className="f-grid">
                  <label className="f-label">Full name *<input data-testid="query-name-input" required minLength={2} name="name" value={qForm.name} onChange={qUpdate} placeholder="Your name" /></label>
                  <label className="f-label">Phone number *<input data-testid="query-phone-input" required minLength={7} name="phone" value={qForm.phone} onChange={qUpdate} placeholder="+91" /></label>
                  <label className="f-label wide">Your question *<textarea data-testid="query-message-input" required minLength={5} name="message" value={qForm.message} onChange={qUpdate} placeholder="Pricing, availability, maintenance, AMC terms… (min 5 characters)" rows={4} /></label>
                </div>
                <button data-testid="query-submit-button" className="btn btn-amber submit" disabled={qLoading} type="submit">
                  {qLoading ? "Sending…" : "Send query"} <ArrowUpRight size={17} />
                </button>
                {qError && (
                  <p className="form-error" data-testid="query-error-message">
                    Could not send: {qError}. <a data-testid="query-whatsapp-fallback" href={whatsappQuery} target="_blank" rel="noreferrer">Send it on WhatsApp instead →</a>
                  </p>
                )}
                <p className="privacy">We usually reply within a few hours on working days.</p>
              </form>
            )
          ) : done ? (
            <div className="success" data-testid="booking-success-message">
              <div className="success-icon"><Check size={26} /></div>
              <h3>Request received.</h3>
              <p>Thank you, {done.name}. Your request is with our team — expect a call shortly.</p>
              {done.paid ? (
                <p className="paid-line" data-testid="advance-paid-message"><Check size={15} /> Booking advance paid.</p>
              ) : payCfg?.enabled ? (
                <button data-testid="pay-advance-button" className="btn btn-amber" onClick={payAdvance} disabled={paying}>
                  <IndianRupee size={16} /> {paying ? "Opening payment…" : `Pay booking advance ₹${(payCfg.advance_amount / 100).toLocaleString("en-IN")}`}
                </button>
              ) : null}
              <button data-testid="new-booking-button" className="btn btn-ghost" onClick={() => { setDone(null); setForm(EMPTY); }}>Send another request</button>
            </div>
          ) : (
            <form data-testid="rental-booking-form" className="bform" onSubmit={submit}>
              <p className="f-step"><span>01</span> YOUR DETAILS</p>
              <div className="f-grid">
                <label className="f-label">Full name *<input data-testid="booking-name-input" required name="name" value={form.name} onChange={update} placeholder="Your name" /></label>
                <label className="f-label">Phone number *<input data-testid="booking-phone-input" required name="phone" value={form.phone} onChange={update} placeholder="+91" /></label>
                <label className="f-label">Email address *<input data-testid="booking-email-input" required type="email" name="email" value={form.email} onChange={update} placeholder="you@company.com" /></label>
                <label className="f-label">Company / organization<input data-testid="booking-company-input" name="company" value={form.company} onChange={update} placeholder="Company name" /></label>
                <label className="f-label wide">Location / site address *<input data-testid="booking-address-input" required name="site_address" value={form.site_address} onChange={update} placeholder="Where should we deliver?" /></label>
              </div>
              <p className="f-step"><span>02</span> RENTAL REQUIREMENTS</p>
              <div className="f-grid">
                <label className="f-label">Required capacity *
                  <select data-testid="booking-capacity-select" required name="required_capacity" value={form.required_capacity} onChange={update}>
                    <option value="">Select kVA</option>
                    {["15 kVA", "30 kVA", "62.5 kVA", "125 kVA", "250 kVA", "500 kVA", "Not sure — please advise"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </label>
                <label className="f-label">Start date *<input data-testid="booking-date-input" required type="date" name="start_date" value={form.start_date} onChange={update} /></label>
                <label className="f-label">Rental duration *<input data-testid="booking-duration-input" required name="rental_duration" value={form.rental_duration} onChange={update} placeholder="e.g. 7 days" /></label>
                <label className="f-label">Anything else?<input data-testid="booking-notes-input" name="notes" value={form.notes} onChange={update} placeholder="Load, timing, special needs…" /></label>
              </div>
              <button data-testid="booking-submit-button" className="btn btn-amber submit" disabled={loading} type="submit">
                {loading ? "Sending request…" : "Send rental request"} <ArrowUpRight size={17} />
              </button>
              <p className="privacy">By submitting, you agree our team may contact you about this request.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
